import { safeParseFloat } from "encamp-shared/src/utils/numberParsing/safeParseNumber";

/**
 * An optionally signed decimal, and nothing else.
 *
 * Commas are matched here rather than stripped before the test, because
 * `safeParseFloat` strips them: stripping first would accept "1,2" and read it as
 * 12, and would reduce a lone "," to blank and clear an existing measurement.
 * Only commas in thousands positions pass, which keeps "1,000" working — the
 * separator people actually type.
 */
const NUMERIC_PATTERN = /^[-+]?((\d+|\d{1,3}(,\d{3})+)(\.\d*)?|\.\d+)$/;

/**
 * A text input's value as a `FlexibleFloat` argument.
 *
 * A string the whole of which is a number becomes one; blank becomes null; and
 * anything else is handed on untouched, so the compliance engine can refuse it by
 * name rather than this silently deciding what the user meant. `safeParseFloat`
 * alone cannot do that job — it wraps `parseFloat`, which takes a numeric prefix,
 * so "12abc" would come back as 12 and replace what someone typed with a value
 * they never entered.
 *
 * Shared by the profile edit form and the extraction review grid so the two
 * cannot disagree about what a number is. They did: the grid took "1,000" and the
 * edit form refused to save it.
 */
export function toFlexibleNumber(value: unknown): number | string | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (trimmed === "") return null;
  if (!NUMERIC_PATTERN.test(trimmed)) return value;

  const parsed = safeParseFloat(trimmed);
  return parsed != null && Number.isFinite(parsed) ? parsed : value;
}
